import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { hasFullPracticeAccess } from '../api/authApi';
import { syncTrackingApi, EngineUnavailableError } from '../api/syncTrackingApi';
import { useLocationContext } from './LocationContext';

interface SyncTrackingContextValue {
    /** Record count per status for the current location — drives the summary tiles. */
    counts: Record<string, number>;
    loading: boolean;
    error: string | null;
    engineUnavailable: boolean;
    /** Count of blocked + dead-letter records — drives the sidebar attention badge. */
    attentionCount: number;
    reload: () => void;
}

const SyncTrackingContext = createContext<SyncTrackingContextValue | null>(null);

/**
 * Fetches per-status sync-tracking counts once per location, shared by the
 * sidebar badge and the Sync Tracking page's summary tiles — these must stay
 * accurate regardless of whatever filter is applied to the page's table, which
 * fetches its own (paginated + filtered) rows directly.
 *
 * Practice-scoped users never call stats (API is full-access only); counts stay empty.
 */
export const SyncTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { locationId, currentLocation } = useLocationContext();
    const emr = currentLocation?.emr ?? '';
    const canSeeTracking = hasFullPracticeAccess();

    const [counts, setCounts] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [engineUnavailable, setEngineUnavailable] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const reload = useCallback(() => setReloadKey(k => k + 1), []);

    useEffect(() => {
        if (!canSeeTracking || !locationId || !emr) {
            setCounts({});
            setLoading(false);
            setError(null);
            setEngineUnavailable(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setEngineUnavailable(false);

        syncTrackingApi.stats(locationId, emr)
            .then(res => {
                if (cancelled) return;
                setCounts(res.counts);
            })
            .catch(err => {
                if (cancelled) return;
                setCounts({});
                if (err instanceof EngineUnavailableError) {
                    setEngineUnavailable(true);
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to load sync tracking');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [locationId, emr, reloadKey, canSeeTracking]);

    const attentionCount = (counts.blocked ?? 0) + (counts.dead_letter ?? 0);

    return (
        <SyncTrackingContext.Provider value={{ counts, loading, error, engineUnavailable, attentionCount, reload }}>
            {children}
        </SyncTrackingContext.Provider>
    );
};

export function useSyncTracking(): SyncTrackingContextValue {
    const ctx = useContext(SyncTrackingContext);
    if (!ctx) throw new Error('useSyncTracking must be used within a SyncTrackingProvider');
    return ctx;
}
